import { ConfirmReloadModal } from "@/components/commons/ConfirmReloadModal";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useDialog } from "@/hooks/useDialog";
import { ClientRoutes } from "@/routes/config";
import { authTokenManager } from "@/services/auth-token-manager";
import { setAxiosAuth } from "@/utils/api";
import { OAUTH_LOGIN_REASONS } from "@/utils/oauthTelemetry";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { verifyToken } from "@/utils/token";
import { Typography } from "@mui/material";
import Cookies from "js-cookie";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const [userId] = useState(Cookies.get("userId"));
  const confirmReload = useDialog();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Periodically check if another account logged in on this browser (multi-account detection).
    // Only redirect if the userId cookie changed to a DIFFERENT value (not empty/missing),
    // to avoid false positives from transient cookie states during token refresh.
    intervalRef.current = setInterval(() => {
      const currentUserId = Cookies.get("userId");
      if (userId && currentUserId && userId !== currentUserId) {
        // Stop the timer before leaving: the redirect is async, so a further tick
        // would fire another hand-off and burn through the loop breaker's budget.
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        redirectToLogin({ reason: OAUTH_LOGIN_REASONS.accountSwitched });
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userId]);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", {
          headers: { "Cache-Control": "no-cache" },
        });
        const { version } = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        // show modal and update version
        if (version && (!currentVersion || version !== parseInt(currentVersion))) {
          localStorage.setItem("app-version", version);
          const agree = await confirmReload.show();
          if (agree) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    const init = async () => {
      const token = Cookies.get("match_token");
      setAxiosAuth();
      let isAuthenticated = !!token && verifyToken(token);

      // Access token missing or expired: try a refresh once before bouncing to
      // login, so a reload on an expired token keeps the session alive.
      if (!isAuthenticated) {
        const newToken = await authTokenManager.refreshToken();
        isAuthenticated = !!newToken && verifyToken(newToken);
        setAxiosAuth();
      }

      if (!isAuthenticated && ClientRoutes.some((path) => pathname.startsWith(path))) {
        redirectToLogin({ clearToken: true, reason: OAUTH_LOGIN_REASONS.unauthenticated });
        return;
      }

      setIsVerified(true);
      await checkVersion();
    };

    init();
  }, [pathname]);

  if (!isVerified) return <LoadingScreen />;

  return (
    <>
      <ConfirmReloadModal open={confirmReload.open} onOk={confirmReload.confirm}>
        <Typography>システムがアップデートされました。最新の状態にするため、ページをリロードしてください。</Typography>
      </ConfirmReloadModal>
      <WrappedComponent {...props} />
    </>
  );
};
